import React, { useState, useEffect, useRef } from 'react';

const CustomGanttChart = ({
  data = [],
  taskFields = {},
  columns = [],
  height = '450px',
  timelineViewMode = 'Week',
  allowSelection = true,
  highlightWeekends = true,
  showTooltip = true,
  originalGanttData = [] // Add this line
}) => {
  const [timelineMode, setTimelineMode] = useState(timelineViewMode);
  const [selectedTask, setSelectedTask] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [tooltip, setTooltip] = useState({ visible: false, content: '', x: 0, y: 0 });
  const [splitterPosition, setSplitterPosition] = useState(600); // Initial width of left panel
  const [isResizing, setIsResizing] = useState(false);
  const ganttRef = useRef(null);
  const splitterRef = useRef(null);


  // Helper function to format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };


  // Default task fields mapping
  const defaultTaskFields = {
    id: 'TaskID',
    name: 'TaskName',
    startDate: 'StartDate',
    endDate: 'EndDate',
    duration: 'Duration',
    progress: 'Progress',
    resourceInfo: 'TeamMember',
    child: 'subtasks',
    dependency: 'Predecessor'
  };

  const mergedTaskFields = { ...defaultTaskFields, ...taskFields };

  // Calculate date range
  useEffect(() => {
    calculateDateRange();
  }, [data]);

  // Handle splitter drag
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const containerRect = ganttRef.current.getBoundingClientRect();
      const newPosition = e.clientX - containerRect.left;
      setSplitterPosition(Math.max(600, Math.min(newPosition, 600))); // Limit between 200px and 600px
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const calculateDateRange = () => {
    if (!data || data.length === 0) {
      setDateRange({ start: new Date(), end: new Date() });
      return;
    }

    let minDate = new Date(data[0][mergedTaskFields.startDate]);
    let maxDate = new Date(data[0][mergedTaskFields.endDate]);

    const traverseTasks = (tasks) => {
      tasks.forEach(task => {
        const startDate = new Date(task[mergedTaskFields.startDate]);
        const endDate = new Date(task[mergedTaskFields.endDate]);

        if (startDate < minDate) minDate = startDate;
        if (endDate > maxDate) maxDate = endDate;

        if (task[mergedTaskFields.child]) {
          traverseTasks(task[mergedTaskFields.child]);
        }
      });
    };

    traverseTasks(data);

    // Add buffer to the date range
    minDate = new Date(minDate.setDate(minDate.getDate() - 7));
    maxDate = new Date(maxDate.setDate(maxDate.getDate() + 7));

    setDateRange({ start: minDate, end: maxDate });
  };

  const handleTaskClick = (task) => {
    if (allowSelection) {
      setSelectedTask(task);
    }
  };

  const handleZoom = (direction) => {
    setZoomLevel(prev => {
      const newZoom = direction === 'in' ? prev * 1.2 : prev / 1.2;
      return Math.min(Math.max(newZoom, 0.5), 3);
    });
  };

  // const showTaskTooltip = (task, event) => {
  //   if (!showTooltip) return;

  //   const tooltipContent = `
  //   <strong>${task[mergedTaskFields.name]}</strong><br/>
  //   Start: ${new Date(task[mergedTaskFields.startDate]).toLocaleDateString()}<br/>
  //   End: ${new Date(task[mergedTaskFields.endDate]).toLocaleDateString()}<br/>
  //   Progress: ${task[mergedTaskFields.progress] || 0}%<br/>
  //   ${task[mergedTaskFields.resourceInfo] ? `Team: ${task[mergedTaskFields.resourceInfo]}` : ''}
  // `;

  //   setTooltip({
  //     visible: true,
  //     content: tooltipContent,
  //     x: event.clientX,
  //     y: event.clientY
  //   });
  // };

  const showTaskTooltip = (task, event) => {
    if (!showTooltip) return;

    // Find if this task has any associated events
    let eventInfo = '';
    if (originalGanttData && task.projectId) {
      const project = originalGanttData.find(p => p._id === task.projectId);
      if (project && project.event && project.event.length > 0) {
        eventInfo = project.event.map(evt =>
          `<span style="color: #ff6b6b; font-weight: bold;">Event: ${evt.title}</span><br/>
         Date: ${formatDate(evt.date)}<br/>
         Status: ${evt.status}<br/>`
        ).join('<br/>');
      }
    }

    const tooltipContent = `
    <strong>${task[mergedTaskFields.name]}</strong><br/>
    Start: ${formatDate(task[mergedTaskFields.startDate])}<br/>
    End: ${formatDate(task[mergedTaskFields.endDate])}<br/>
    Progress: ${task[mergedTaskFields.progress] || 0}%<br/>
    ${task[mergedTaskFields.resourceInfo] ? `Team: ${task[mergedTaskFields.resourceInfo]}` : ''}
    ${eventInfo ? `<br/><hr/>${eventInfo}` : ''}
  `;

    setTooltip({
      visible: true,
      content: tooltipContent,
      x: event.clientX,
      y: event.clientY
    });
  };

  const hideTooltip = () => {
    setTooltip({ ...tooltip, visible: false });
  };

  const renderTimelineHeader = () => {
    if (!dateRange.start || !dateRange.end) return null;

    const timeUnits = [];
    const currentDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);

    if (timelineMode === 'Day') {
      while (currentDate <= endDate) {
        const day = currentDate.getDate();
        const month = currentDate.toLocaleString('default', { month: 'short' });
        const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;

        timeUnits.push(
          <div
            key={currentDate.toISOString()}
            className={`timeline-day ${isWeekend && highlightWeekends ? 'weekend' : ''}`}
            style={{ width: `${40 * zoomLevel}px` }}
          >
            <div className="timeline-day-number">{day}</div>
            <div className="timeline-month">{month}</div>
          </div>
        );
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else if (timelineMode === 'Week') {
      while (currentDate <= endDate) {
        const weekStart = new Date(currentDate);
        const weekEnd = new Date(currentDate);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const month = currentDate.toLocaleString('default', { month: 'short' });
        const year = currentDate.getFullYear();

        timeUnits.push(
          <div
            key={currentDate.toISOString()}
            className="timeline-week"
            style={{ width: `${80 * zoomLevel}px` }}
          >
            <div className="timeline-week-range">
              {weekStart.getDate()} - {weekEnd.getDate()}
            </div>
            <div className="timeline-month-year">
              {month} {year !== new Date().getFullYear() ? year : ''}
            </div>
          </div>
        );
        currentDate.setDate(currentDate.getDate() + 7);
      }
    } else { // Month
      while (currentDate <= endDate) {
        const month = currentDate.toLocaleString('default', { month: 'short' });
        const year = currentDate.getFullYear();

        timeUnits.push(
          <div
            key={currentDate.toISOString()}
            className="timeline-month"
            style={{ width: `${120 * zoomLevel}px` }}
          >
            <div className="timeline-month-name">{month}</div>
            <div className="timeline-year">{year !== new Date().getFullYear() ? year : ''}</div>
          </div>
        );
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
    }

    return timeUnits;
  };


  // const renderTimelineHeader = () => {
  //   if (!data || data.length === 0) return null;

  //   // Calculate min/max dates from the data
  //   let minDate = new Date(data[0][mergedTaskFields.startDate]);
  //   let maxDate = new Date(data[0][mergedTaskFields.endDate]);

  //   data.forEach(task => {
  //     const startDate = new Date(task[mergedTaskFields.startDate]);
  //     const endDate = new Date(task[mergedTaskFields.endDate]);

  //     if (startDate < minDate) minDate = startDate;
  //     if (endDate > maxDate) maxDate = endDate;
  //   });

  //   // Add buffer to the date range
  //   minDate = new Date(minDate.setDate(minDate.getDate() - 7));
  //   maxDate = new Date(maxDate.setDate(maxDate.getDate() + 7));

  //   const timeUnits = [];
  //   const currentDate = new Date(minDate);
  //   const endDate = new Date(maxDate);

  //   if (timelineMode === 'Day') {
  //     while (currentDate <= endDate) {
  //       const day = currentDate.getDate();
  //       const month = currentDate.toLocaleString('default', { month: 'short' });
  //       const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;

  //       timeUnits.push(
  //         <div
  //           key={currentDate.toISOString()}
  //           className={`timeline-day ${isWeekend && highlightWeekends ? 'weekend' : ''}`}
  //           style={{ width: `${40 * zoomLevel}px` }}
  //         >
  //           <div className="timeline-day-number">{day}</div>
  //           <div className="timeline-month">{month}</div>
  //         </div>
  //       );
  //       currentDate.setDate(currentDate.getDate() + 1);
  //     }
  //   } else if (timelineMode === 'Week') {
  //     // Adjust to start of week (Sunday)
  //     currentDate.setDate(currentDate.getDate() - currentDate.getDay());
  //     endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

  //     while (currentDate <= endDate) {
  //       const weekStart = new Date(currentDate);
  //       const weekEnd = new Date(currentDate);
  //       weekEnd.setDate(weekEnd.getDate() + 6);

  //       const month = currentDate.toLocaleString('default', { month: 'short' });
  //       const year = currentDate.getFullYear();

  //       timeUnits.push(
  //         <div
  //           key={currentDate.toISOString()}
  //           className="timeline-week"
  //           style={{
  //             width: `${80 * zoomLevel}px`,
  //             flexShrink: 0 // Prevent flexbox from shrinking the weeks
  //           }}
  //         >
  //           <div className="timeline-week-range">
  //             {weekStart.getDate()} - {weekEnd.getDate()}
  //           </div>
  //           <div className="timeline-month-year">
  //             {month} {year !== new Date().getFullYear() ? year : ''}
  //           </div>
  //         </div>
  //       );
  //       currentDate.setDate(currentDate.getDate() + 7);
  //     }
  //   } else { // Month
  //     // Adjust to start of month
  //     currentDate.setDate(1);
  //     endDate.setDate(1);
  //     endDate.setMonth(endDate.getMonth() + 1);

  //     while (currentDate <= endDate) {
  //       const month = currentDate.toLocaleString('default', { month: 'short' });
  //       const year = currentDate.getFullYear();

  //       timeUnits.push(
  //         <div
  //           key={currentDate.toISOString()}
  //           className="timeline-month"
  //           style={{ width: `${120 * zoomLevel}px` }}
  //         >
  //           <div className="timeline-month-name">{month}</div>
  //           <div className="timeline-year">{year !== new Date().getFullYear() ? year : ''}</div>
  //         </div>
  //       );
  //       currentDate.setMonth(currentDate.getMonth() + 1);
  //     }
  //   }

  //   return timeUnits;
  // };

  const calculateTaskPosition = (task) => {
    if (!dateRange.start || !dateRange.end) return { left: 0, width: 0 };

    const startDate = new Date(task[mergedTaskFields.startDate]);
    const endDate = new Date(task[mergedTaskFields.endDate]);
    const totalDays = (dateRange.end - dateRange.start) / (1000 * 60 * 60 * 24);
    const taskDays = (endDate - startDate) / (1000 * 60 * 60 * 24);

    const left = ((startDate - dateRange.start) / (1000 * 60 * 60 * 24)) / totalDays * 100;
    const width = (taskDays / totalDays) * 100;

    return { left, width };
  };


  // const calculateTaskPosition = (task) => {
  //   if (!dateRange.start || !dateRange.end) return { left: 0, width: 0 };

  //   const startDate = new Date(task[mergedTaskFields.startDate]);
  //   const endDate = new Date(task[mergedTaskFields.endDate]);

  //   if (timelineMode === 'Week') {
  //     // For week view, we need to align with week boundaries
  //     const visibleStart = new Date(dateRange.start);
  //     const visibleEnd = new Date(dateRange.end);

  //     // Adjust to start of week (Sunday)
  //     visibleStart.setDate(visibleStart.getDate() - visibleStart.getDay());
  //     visibleEnd.setDate(visibleEnd.getDate() + (6 - visibleEnd.getDay()));

  //     const totalWeeks = Math.ceil((visibleEnd - visibleStart) / (7 * 24 * 60 * 60 * 1000));

  //     // Adjust task dates to week boundaries
  //     const taskStartWeek = Math.floor((startDate - visibleStart) / (7 * 24 * 60 * 60 * 1000));
  //     const taskEndWeek = Math.ceil((endDate - visibleStart) / (7 * 24 * 60 * 60 * 1000));

  //     const left = (taskStartWeek / totalWeeks) * 100;
  //     const width = ((taskEndWeek - taskStartWeek) / totalWeeks) * 100;

  //     return {
  //       left: Math.max(0, Math.min(left, 100)),
  //       width: Math.max(0, Math.min(width, 100 - left))
  //     };
  //   } else {
  //     // Original day-based calculation for other views
  //     const visibleStart = dateRange.start;
  //     const visibleEnd = dateRange.end;
  //     const totalVisibleDays = Math.ceil((visibleEnd - visibleStart) / (1000 * 60 * 60 * 24));

  //     const taskStartDay = Math.max(0, Math.ceil((startDate - visibleStart) / (1000 * 60 * 60 * 24)));
  //     const taskEndDay = Math.min(totalVisibleDays, Math.ceil((endDate - visibleStart) / (1000 * 60 * 60 * 24)));

  //     const taskDurationDays = taskEndDay - taskStartDay;

  //     const left = (taskStartDay / totalVisibleDays) * 100;
  //     const width = (taskDurationDays / totalVisibleDays) * 100;

  //     return {
  //       left: Math.max(0, Math.min(left, 100)),
  //       width: Math.max(0, Math.min(width, 100 - left))
  //     };
  //   }
  // };

  // Modify the columns to include formatted dates if they're date fields
  const getDisplayValue = (task, field) => {
    if (field === 'StartDate' || field === 'EndDate') {
      return formatDate(task[field]);
    }
    return task[field] || '-';
  };

  const renderTaskBars = (tasks, level = 0) => {
    return tasks.map((task, index) => {
      const position = calculateTaskPosition(task);
      const progress = task[mergedTaskFields.progress] || 0;
      const isSelected = selectedTask && selectedTask[mergedTaskFields.id] === task[mergedTaskFields.id];

      return (
        <React.Fragment key={task[mergedTaskFields.id] || index}>
          <div
            className={`task-row ${isSelected ? 'selected' : ''}`}
            style={{ paddingLeft: `${level * 20}px` }}
          >
            {/* Left side - Task details */}
            <div className="task-details" style={{ width: splitterPosition }}>
              {columns.map((col, colIndex) => (
                <div key={colIndex} className="task-cell" style={{ width: col.width || 'auto' }}>
                  {getDisplayValue(task, col.field)}
                </div>
              ))}
            </div>

            {/* Right side - Timeline bar */}
            <div className="task-bar-container" style={{ width: `calc(100% - ${splitterPosition}px)` }}>
              <div
                className="task-bar"
                style={{
                  left: `${position.left + position.left * 5}px `,
                  width: `${position.width + (position.width + 100)}px`
                }}
                onClick={(e) => handleTaskClick(task)}
                onMouseEnter={(e) => showTaskTooltip(task, e)}
                onMouseLeave={hideTooltip}
              >
                <div
                  className="task-progress"
                  style={{ width: `${progress}%` }}
                ></div>
                <div className="task-label">
                  {task[mergedTaskFields.name]} ({progress}%)
                </div>
              </div>
            </div>
          </div>
          {task[mergedTaskFields.child] && renderTaskBars(task[mergedTaskFields.child], level + 1)}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="custom-gantt-container" ref={ganttRef} style={{ height }}>
      <div className="gantt-controls">
        <div className="view-mode-selector">
          <button
            className={timelineMode === 'Day' ? 'active' : ''}
            onClick={() => setTimelineMode('Day')}
          >
            Day
          </button>
          <button
            className={timelineMode === 'Week' ? 'active' : ''}
            onClick={() => setTimelineMode('Week')}
          >
            Week
          </button>
          <button
            className={timelineMode === 'Month' ? 'active' : ''}
            onClick={() => setTimelineMode('Month')}
          >
            Month
          </button>
        </div>
        <div className="zoom-controls">
          <button onClick={() => handleZoom('out')}>-</button>
          <span>{Math.round(zoomLevel * 100)}%</span>
          <button onClick={() => handleZoom('in')}>+</button>
        </div>
      </div>

      <div className="gantt-grid">
        <div className="grid-header">
          <div className="columns-header">
            {/* Left side - Column headers */}
            <div className="task-details-header" style={{ width: splitterPosition }}>
              {columns.map((col, index) => (
                <div key={index} className="column-header" style={{ width: col.width || 'auto' }}>
                  {col.headerText || col.field}
                </div>
              ))}
            </div>

            {/* Right side - Timeline header */}
            <div className="timeline-header" style={{ width: `calc(100% - ${splitterPosition}px)` }}>
              {renderTimelineHeader()}
            </div>
          </div>
        </div>

        <div className="grid-body">
          {data && data.length > 0 ? (
            renderTaskBars(data)
          ) : (
            <div className="no-data-message">No tasks to display</div>
          )}
        </div>
      </div>

      {/* Splitter */}
      <div
        className="gantt-splitter"
        ref={splitterRef}
        style={{ left: `${splitterPosition}px` }}
        onMouseDown={() => setIsResizing(true)}
      />

      {tooltip.visible && (
        <div
          className="gantt-tooltip"
          style={{
            position: 'fixed',
            left: `${tooltip.x + 10}px`,
            top: `${tooltip.y + 10}px`,
            zIndex: 1000
          }}
          dangerouslySetInnerHTML={{ __html: tooltip.content }}
        />
      )}


    </div>
  );
};

export default CustomGanttChart;